/**
 * Tap mechanic — the heart of the game. Mechanic A from `docs/02`.
 *
 * Tap value formula (`docs/09` §I.2):
 *   tapValue = base × layerBonus × specialBonus × globalMultiplier
 *              × activeMechanicBonus × streak × jitter
 *
 * Where:
 *   - base                 = per-epoch BaseValue
 *   - layerBonus           = PHRASE_LAYER_MULTIPLIERS[phrase.layer]
 *                            (numeric ×1, sensory ×2, body ×3, elemental ×5,
 *                             awareness ×10, program ×20, creator ×50)
 *   - specialBonus         = SPECIAL_TAP_MULTIPLIERS[phrase.special] when set
 *                            (on10thTap ×5, on100thTap ×25, on1000thTap ×100)
 *   - globalMultiplier     = total composed from lessons, atlas, guides, …
 *   - activeMechanicBonus  = manual-mode meta-strategy
 *   - streak               = rhythm bonus in [1, 2]
 *   - jitter               = uniform random in [TAP_RANDOM_MIN, TAP_RANDOM_MAX]
 *
 * The floating phrase is more than decoration — its layer drives the actual
 * gain. "+1" yields 1 awareness; "вижу" yields ~2; "я слышу" yields ~10;
 * a 1000th-tap milestone phrase yields ~1000. Players don't see numbers on
 * word phrases, but the counter ticks up by the real amount.
 */
import {
  getBaseTap,
  PHRASE_LAYER_MULTIPLIERS,
  SPECIAL_TAP_INTERVALS,
  SPECIAL_TAP_MULTIPLIERS,
  TAP_RANDOM_MAX,
  TAP_RANDOM_MIN,
} from './constants.js';
import { defaultRandom, pickWeighted, type RandomSource } from './rng.js';
import type { Epoch, PhraseEntry, PhraseLayer } from './types.js';

export interface ComputeTapInput {
  readonly epoch: Epoch;
  readonly globalMultiplier: number;
  /** Phrase layer drives the per-tap bonus. Defaults to 'numeric' (×1). */
  readonly phraseLayer?: PhraseLayer;
  /** Milestone bonus on top of the layer bonus. */
  readonly special?: PhraseEntry['special'];
  /** From active-mechanic bonus (manual-mode meta). Defaults to 1.0. */
  readonly activeMechanicBonus?: number;
  /** Streak multiplier in [1, 2]. Defaults to 1.0. */
  readonly streak?: number;
  readonly random?: RandomSource;
}

export interface ComputeTapOutput {
  readonly value: number; // integer awareness gain, ≥ 1
  readonly base: number;
  readonly layerBonus: number;
  readonly specialBonus: number;
  readonly multiplier: number; // composite of global × active × streak
  readonly jitter: number;
}

/**
 * Compute how many Awarenesses a single tap yields.
 *
 * The final value is floored to a non-negative integer with a minimum of 1 —
 * "every tap counts" is a core feel guarantee.
 */
export function computeTapValue(input: ComputeTapInput): ComputeTapOutput {
  const base = getBaseTap(input.epoch);
  const layerBonus = PHRASE_LAYER_MULTIPLIERS[input.phraseLayer ?? 'numeric'];
  const specialBonus = input.special !== undefined ? SPECIAL_TAP_MULTIPLIERS[input.special] : 1;
  const multiplier = sanitizeMultiplier(input.globalMultiplier);
  const active = sanitizeMultiplier(input.activeMechanicBonus ?? 1);
  const streak = clamp(input.streak ?? 1, 1, 2);
  const jitter = randomJitter(input.random ?? defaultRandom);

  const raw = base * layerBonus * specialBonus * multiplier * active * streak * jitter;
  const value = Math.max(1, Math.floor(raw));

  return {
    value,
    base,
    layerBonus,
    specialBonus,
    multiplier: multiplier * active * streak,
    jitter,
  };
}

// ---------------------------------------------------------------------------
// Phrase selection
// ---------------------------------------------------------------------------

export interface SelectPhraseInput {
  readonly tapIndex: number; // 1-indexed total tap count (post-increment)
  readonly availablePhrases: readonly PhraseEntry[];
  readonly random?: RandomSource;
  /**
   * Per-phrase sighting counts (atlasCollected Map). When provided, phrase
   * weights are damped by how many times the player has already seen each
   * phrase — variety without erasing repeats. Doc 28 §3.
   *
   * If omitted (or empty), behavior is identical to plain weighted draw.
   */
  readonly sightingCounts?: ReadonlyMap<string, number>;
}

/**
 * How much each repeat sighting nudges down a phrase's effective weight.
 * effectiveWeight = baseWeight / (1 + count * K) — clamped to a floor.
 *
 * K=0.15 means:
 *   count=0  → 1.00× (full weight, never-seen prioritized organically)
 *   count=3  → 0.69×
 *   count=10 → 0.40×
 *   count=30 → 0.18×
 * Combined with the floor, a heavily-seen phrase still appears, just rarely.
 *
 * Gentle: cozy/wholesome — the player feels variety, not whiplash.
 */
const ANTI_REPEAT_K = 0.15;

/**
 * Hard floor on the damping factor — even a phrase the player has seen
 * 100 times still gets 15 % of its base weight. We want variety, not
 * silence; every phrase remains reachable.
 */
const ANTI_REPEAT_FLOOR = 0.15;

/**
 * Choose which floating phrase appears for this tap.
 * Milestone taps (10th, 100th, …) draw from `special` phrases; other taps
 * draw weighted from the normal pool.
 *
 * When `sightingCounts` is provided, each candidate's weight is damped by
 * how many times the player has already seen it — favoring variety
 * without ever silencing a phrase. Doc 28 §3 anti-repeat.
 */
export function selectPhrase(input: SelectPhraseInput): PhraseEntry {
  const random = input.random ?? defaultRandom;

  // Milestone first — order matters because larger milestones contain smaller ones.
  const milestones = [
    { key: 'on1000thTap' as const, every: SPECIAL_TAP_INTERVALS.on1000thTap },
    { key: 'on100thTap' as const, every: SPECIAL_TAP_INTERVALS.on100thTap },
    { key: 'on10thTap' as const, every: SPECIAL_TAP_INTERVALS.on10thTap },
  ];

  for (const m of milestones) {
    if (input.tapIndex % m.every === 0) {
      const specials = input.availablePhrases.filter((p) => p.special === m.key);
      if (specials.length > 0) {
        // Weighted among the matching specials (usually just one) — also damped.
        return pickWeighted(applyAntiRepeat(specials, input.sightingCounts), random);
      }
    }
  }

  // Normal weighted draw — exclude specials (they only fire on milestones)
  const normal = input.availablePhrases.filter((p) => p.special === undefined);
  if (normal.length === 0) {
    throw new RangeError('selectPhrase: no non-special phrases available');
  }
  return pickWeighted(applyAntiRepeat(normal, input.sightingCounts), random);
}

/**
 * Return a parallel array of `{ ...phrase, weight: dampedWeight }` so
 * pickWeighted can see the adjusted distribution. When `sightingCounts`
 * is undefined or empty, weights are unchanged (identical to the legacy
 * behavior).
 *
 * Pure / deterministic — the random pick happens downstream in pickWeighted.
 */
function applyAntiRepeat(
  phrases: readonly PhraseEntry[],
  sightingCounts: ReadonlyMap<string, number> | undefined,
): readonly PhraseEntry[] {
  if (sightingCounts === undefined || sightingCounts.size === 0) {
    return phrases;
  }
  return phrases.map((p) => {
    const count = sightingCounts.get(p.id) ?? 0;
    if (count === 0) return p; // never-seen — keep full weight
    const factor = Math.max(ANTI_REPEAT_FLOOR, 1 / (1 + count * ANTI_REPEAT_K));
    return { ...p, weight: p.weight * factor };
  });
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

function randomJitter(random: RandomSource): number {
  const r = random();
  return TAP_RANDOM_MIN + r * (TAP_RANDOM_MAX - TAP_RANDOM_MIN);
}

function sanitizeMultiplier(n: number): number {
  return n > 0 && Number.isFinite(n) ? n : 1;
}

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) {
    return min;
  }
  return Math.min(Math.max(n, min), max);
}
